import ReportFigure from '@components/Reporting/ReportFigure';
import SeatUsageChart from '@components/Reporting/SeatUsageChart';
import Header from '@components/Shared/Layout/Header';
import { Loading, LoadFailed } from '@components/Shared/LoadState';
import { ROUTE_PATHS } from '@constants/routes';
import { addUtcDays, formatUtcDay, utcDayOf, utcDaysBetween } from '@constants/utcDay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import { Alert, Box, Button, Card, CardContent, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useGetLicensesQuery, type License } from '@store/api/licenseEndpoints';
import {
  useGetSeatUsageQuery, type SeatUsageApp, type SeatUsageDay, type SeatUsageHistory, type SeatUsageSummary
} from '@store/api/seatUsageEndpoints';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

const RANGES = [
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
  { value: '365', label: 'Last 12 months', days: 365 },
  { value: 'all', label: 'All', days: undefined },
] as const;

type Range = typeof RANGES[number]['value'];

const APPS: { app: SeatUsageApp, name: string }[] = [
  { app: 'supar', name: 'SuPAR' },
  { app: 'composer', name: 'Composer' },
];

const limitOf = (license: License, app: SeatUsageApp) =>
  app === 'supar' ? license.deviceLimitCount : license.composerDeviceLimitCount;

/** The figures of an application nobody has used on this licence yet. */
const unused = (seatLimit: number): SeatUsageSummary => ({
  seatLimit, seatLimitBefore: seatLimit, peakSeatsInUse: 0, peakUsage: 0, daysAtLimit: 0, daysExceeded: 0,
  exceededBy: 0, refusedRequests: 0, offlineSeconds: 0, offlineBeyondLimitSeconds: 0
});

/**
 * Every day of the range for one application. The server sends only days with activity; the rest
 * are zero and carry the limit of the day before them, the first of them the limit from before the
 * range (`seatLimitBefore`, answered for every application with a summary).
 */
const everyDay = (history: SeatUsageHistory, app: SeatUsageApp, fallbackLimit: number): SeatUsageDay[] => {
  const rows = history.days.filter(day => day.app === app);
  const byDate = new Map(rows.map(day => [day.date, day]));
  let seatLimit = history.summary[app]?.seatLimitBefore ?? fallbackLimit;

  return utcDaysBetween(history.from, history.to).map(date => {
    const row = byDate.get(date);

    if (row) {
      seatLimit = row.seatLimit;

      return row;
    }

    return {
      date, app, seatLimit, peakSeatsInUse: 0, refusedRequests: 0, offlineSeconds: 0,
      offlineBeyondLimitSeconds: 0, peakBeyondLimit: 0, atLimit: false, exceeded: false
    };
  });
};

const hasActivity = (days: SeatUsageDay[]) =>
  days.some(day => day.peakSeatsInUse || day.refusedRequests || day.offlineSeconds);

/**
 * Seat usage: how much of one licence was used, day by day, per application.
 *
 * Read from the seat usage history, which the server keeps without a time limit and without
 * anything about a device, so this answers "is this licence the right size" over years where the
 * Activity page answers "what happened" over its retention. Whether a day reached or exceeded the
 * limit is the server's call and is shown, never worked out here.
 */
const SeatUsagePage = () => {
  const { licenseId = '' } = useParams();
  const { data: licenses, isLoading, isError: licensesFailed, refetch: refetchLicenses } = useGetLicensesQuery();
  const [range, setRange] = useState<Range>('90');

  const license = licenses?.find(item => item.licenseId === licenseId);
  const since = license && utcDayOf(new Date(license.createdAt));

  // "All" starts on the day the licence came to this server. The server starts every other range
  // there too when the licence is younger (nothing about it can be older, and a day before it would
  // be drawn at a limit it never had here), and answers where the range starts.
  const query = useMemo(() => {
    const to = utcDayOf(new Date());
    const days = RANGES.find(item => item.value === range)?.days;
    const from = days ? addUtcDays(to, -(days - 1)) : since;

    // A browser clock behind the server's must not ask for a range that ends before it starts.
    return { licenseId, from: from && from > to ? to : from, to };
  }, [range, licenseId, since]);

  // The answer for the range on screen only (`currentData`): `data` would keep the previous range's
  // figures under the new range while it loads, and after the new one failed.
  const {
    currentData: history, isError: historyFailed, refetch: refetchHistory
  } = useGetSeatUsageQuery(query, { skip: !license });

  // Said beside the range, so a "Last 12 months" that shows three is not taken for a fault.
  const startsAtLicense = !!history && !!query.from && history.from > query.from && history.from === since;

  // Until the licence is known there is nothing to draw, and which of three reasons it is gets said:
  // still loading, a list that could not be loaded, or a licence this server does not have. The
  // last used to be said for all three.
  if (!license) {
    return (
      <Box sx={{ width: '100%' }}>
        <Header title='Seat usage' />
        {/* Spaced by the stack: `index.css` zeroes a paragraph's bottom margin. */}
        <Card><CardContent>
          {isLoading ? (
            <Loading text='Loading the license…' />
          ) : licensesFailed ? (
            <LoadFailed text='The licenses could not be loaded.' onRetry={() => { void refetchLicenses(); }} />
          ) : (
            <Stack spacing={2} alignItems='flex-start'>
              <Typography variant='body2' color='text.secondary'>
                This license is not installed on this server.
              </Typography>
              <Button size='small' startIcon={<ArrowBackIcon />} component={RouterLink} to={ROUTE_PATHS.LICENSES}>
                Licenses
              </Button>
            </Stack>
          )}
        </CardContent></Card>
      </Box>
    );
  }

  const apps = history ? APPS.filter(({ app }) => history.summary[app] || limitOf(license, app) > 0) : [];

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Seat usage'
        description={`${license.label || license.id || license.licenseId.slice(0, 8)}: how many of its seats ` +
          'were in use each day, and whether that went beyond them.'}
      >
        <Button
          size='small' startIcon={<CloudOffRoundedIcon />} sx={{ whiteSpace: 'nowrap' }}
          component={RouterLink} to={`${ROUTE_PATHS.OFFLINE_USAGE}?licenseId=${licenseId}`}
        >
          Offline usage
        </Button>
        <Button
          size='small' startIcon={<ArrowBackIcon />} sx={{ whiteSpace: 'nowrap' }}
          component={RouterLink} to={ROUTE_PATHS.LICENSES}
        >
          Licenses
        </Button>
      </Header>

      <Alert severity='info' sx={{ mb: 2 }}>
        Counted per day in UTC and kept without a time limit, with nothing recorded about any device.
        A day without activity counts as zero. Offline time is added to the day it was used when the
        device reports it, which can be days later, so recent days can still change. The tickets and
        allowances behind it are under Offline usage.
      </Alert>

      <Stack direction='row' spacing={2} useFlexGap alignItems='center' sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          select size='small' label='Range' value={range} sx={{ minWidth: 170 }}
          onChange={event => setRange(event.target.value as Range)}
        >
          {RANGES.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>

        {startsAtLicense && history && (
          <Typography variant='caption' color='text.secondary'>
            From {formatUtcDay(history.from)}, the day this license came to this server.
          </Typography>
        )}

        {history?.truncated && (
          <Typography variant='caption' color='text.secondary'>
            Showing the latest five years, from {formatUtcDay(history.from)}. Older days are kept, and one answer holds five years at most.
          </Typography>
        )}
      </Stack>

      {/* Never blank: a range still loading, or one that could not be loaded, says which. */}
      {historyFailed ? (
        <LoadFailed text='The seat usage could not be loaded.' onRetry={() => { void refetchHistory(); }} />
      ) : !history && (
        <Card><CardContent><Loading text='Loading the seat usage…' /></CardContent></Card>
      )}

      {history && apps.length === 0 && (
        <Card><CardContent>
          <Typography variant='body2' color='text.secondary'>This license has no seats to use.</Typography>
        </CardContent></Card>
      )}

      {history && apps.map(({ app, name: appName }) => {
        const summary = history.summary[app] ?? unused(limitOf(license, app));
        const days = everyDay(history, app, limitOf(license, app));

        return (
          <Card key={app} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant='h6' gutterBottom>{appName}</Typography>

              {/* The answer first, in words, before any figure or chart. */}
              {summary.daysExceeded > 0 ? (
                <Alert severity='warning' sx={{ mb: 3 }}>
                  Seat limit exceeded on {summary.daysExceeded.formatCount('day', 'days')}, by up
                  to {summary.exceededBy.formatCount('device', 'devices')}
                </Alert>
              ) : (
                <Alert severity='success' sx={{ mb: 3 }}>Seat limit not exceeded in this period</Alert>
              )}

              {/* A gap rather than margins, so a wrapped second row starts where the first did. */}
              <Stack direction='row' spacing={4} useFlexGap sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
                <ReportFigure label='Seat limit' value={String(summary.seatLimit)} />
                <ReportFigure
                  label='Peak usage'
                  value={String(summary.peakUsage)}
                  hint={summary.peakUsage > summary.peakSeatsInUse
                    ? 'seats held, plus devices offline beyond the limit'
                    : 'seats held at once'}
                />
                <ReportFigure label='Days at the limit' value={String(summary.daysAtLimit)} hint='every seat held, or a request refused' />
                <ReportFigure label='Days exceeded' value={String(summary.daysExceeded)} hint='more in use than licensed' />
                <ReportFigure label='Refused requests' value={String(summary.refusedRequests)} hint='no seat was free' />
                <ReportFigure
                  label='Offline time'
                  value={summary.offlineSeconds ? summary.offlineSeconds.formatDuration() : '-'}
                  hint='reported by devices out of contact'
                />
                <ReportFigure
                  label='Offline beyond the limit'
                  value={summary.offlineBeyondLimitSeconds ? summary.offlineBeyondLimitSeconds.formatDuration() : '-'}
                  hint='while every seat was taken'
                />
              </Stack>

              {hasActivity(days) ? (
                <SeatUsageChart days={days} label={appName} />
              ) : (
                <Typography variant='body2' color='text.secondary'>Nothing recorded in this period.</Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default SeatUsagePage;
